import React, { FC, useMemo } from 'react';
import { RichText, Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import CTAButton, { ctaEnums } from '../@shared/ctaButton';
import { CustomText } from '../@shared/customText';
import RoutableSitecoreLink from '../@shared/routableSitecoreLink';
import Picture from '../@shared/picture';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import { HeroBannerModel } from './models/heroBanner.model';
import { HeroBannerProps } from './types/heroBanner.types';
import { targetLinkdsk } from '../../utils/targetDskLink';
import { googleClickCtaEvent } from '../../utils/analytics';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import { shouldPioritizeComponentResources } from '../../utils/component.utils';
import { getQueryLink } from '../../utils/getQuerylink';

const SmallHero: FC<HeroBannerProps> = (props) => {
  const model = new HeroBannerModel(props);
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx.md})`);
  const placeholderData = props?.sitecoreContext?.route?.placeholders['jss-main'] || [];
  const shouldPioritize = useMemo(() => shouldPioritizeComponentResources(placeholderData, 'SmallHero'), [placeholderData]);
  model?.primaryLink && getQueryLink(model.primaryLink)
  model?.secondaryLink && getQueryLink(model.secondaryLink)

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  return (
    <AnalyticsVisibleEvent
      rendering={props?.rendering}
      title={model.title?.value ?? model.eyebrow?.value}
      template="Hero Banner Small Hero"
    >
      <div
        className={`small-hero hero-banner ${!model?.isGradientDisabled ? 'hero-banner--background' : ''}`}
        data-text-color={model.textColor?.toLowerCase()}
      >
        {/* Need to update useHeroBanner hook if the images sent to Picture component  are changed */}
        <Picture
          editorialImageProps={{
            desktopImage: model?.image,
            mobileImage: model?.mobileImage,
            desktopTransformation: 'Size1900',
            mobileTransformation: 'Size1300',
            alt: model?.image?.value?.alt as string,
            cssClassImageEl: `hero-banner__image ${isDesktop ? 'hero-banner__image--desktop' : 'hero-banner__image--mobile'}`,
            prioritize: shouldPioritize,
          }}
        />
        <div className="hero-banner__content hero-banner__content--align-top hero-banner__content--top-margin">
          {model?.eyebrow && <Text tag="p" field={model?.eyebrow} className={`hero-banner__eyebrow hero-banner--${model.textColor}`} />}
          <CustomText tag={model?.headingTag} field={model?.title} className={`hero-banner__title hero-banner--${model.textColor}`} />
          {model?.description && (
            <RichText
              tag="div"
              field={model?.description}
              className={`hero-banner__description hero-banner--${model.textColor} rich-text color-inherit-link`}
            />
          )}
          <span className={'hero-banner__link hero-banner__link--primary'}>
            {model?.primaryLink?.value?.href && (
              <CTAButton type={ctaEnums.primaryRed}>
                <RoutableSitecoreLink
                  field={model?.primaryLink}
                  target={targetLinkdsk(model?.primaryLink, isDesktop)}
                  onClick={() =>
                    googleClickCtaEvent(
                      'Hero',
                      model?.primaryLink?.value?.text ?? model?.primaryLink?.value?.title,
                      model.title?.value ?? model.eyebrow?.value
                    )
                  }
                />
              </CTAButton>
            )}
          </span>
          <span className={'hero-banner__link hero-banner__link--secondary'}>
            {model?.secondaryLink?.value?.href && (
              <CTAButton type={ctaEnums.primaryWhite}>
                <RoutableSitecoreLink
                  field={model?.secondaryLink}
                  target={targetLinkdsk(model?.secondaryLink, isDesktop)}
                  onClick={() =>
                    googleClickCtaEvent(
                      'Hero',
                      model?.secondaryLink?.value?.text ?? model?.secondaryLink?.value?.title,
                      model.title?.value ?? model.eyebrow?.value
                    )
                  }
                />
              </CTAButton>
            )}
          </span>
        </div>
      </div>
    </AnalyticsVisibleEvent>
  );
};

export default withSitecoreContext()(SmallHero);
